import { FormDefinition, Question, Section } from '../types/form-engine.types';

const stripMarkdownLinks = (text: string) => text?.replace(/\[([^\]]*)\]\(https?:\/\/[^\s)]+\)/g, '$1') ?? text;

export const isAnnualPromotionForm = (assessmentType?: string, reviewType?: string) =>
  assessmentType === 'arf' && reviewType === 'annual';

const PROMOTION_REQUIREMENTS: { flag: 'IsPromotionTierQuestion' | 'IsPromotionRecommendationQuestion'; label: string }[] = [
  { flag: 'IsPromotionTierQuestion', label: 'Suggested Tier' },
  { flag: 'IsPromotionRecommendationQuestion', label: 'Recommended Promotion' },
];

const getAnnualPromotionValidationErrors = (questions: Question[], assessmentType?: string, reviewType?: string) => {
  if (!isAnnualPromotionForm(assessmentType, reviewType)) return [];
  const multipleChoice = questions.filter((question) => question.FormatType === 'multiple-choice');

  return PROMOTION_REQUIREMENTS.reduce<string[]>((errors, requirement) => {
    const matches = multipleChoice.filter((question) => question[requirement.flag]).length;
    if (matches === 0) {
      errors.push(
        `For annual assessment and annual review, mark one multiple-choice question as the '${requirement.label}' question`
      );
    } else if (matches > 1) {
      errors.push(`Only one multiple-choice question can be marked as the '${requirement.label}' question`);
    }
    return errors;
  }, []);
};

/** Mirrors CreatePmFormCommandValidator.HaveAllRequiredLikertQuestions on the legacy backend. */
export const REQUIRED_LIKERT_QUESTION_KEYWORDS = [
  'AI and Frontier Tech',
  'Work Output',
  'Leadership',
  'Improvement Initiative',
  'Culture',
  'Communication',
];

/** Mirrors CreatePmFormCommandValidator.HaveAllRequiredTextQuestions on the legacy backend. */
const REQUIRED_TEXT_QUESTION_KEYWORDS = ['Strengths', 'Areas for Improvement'];

const getRequiredLikertKeywordsValidationErrors = (questions: Question[], assessmentType?: string) => {
  if (assessmentType !== 'arf') return [];
  const texts = questions.filter((q) => q.FormatType === 'likert-scale').map((q) => (q.Question || '').toLowerCase());
  const missing = REQUIRED_LIKERT_QUESTION_KEYWORDS.filter((keyword) => !texts.some((t) => t.includes(keyword.toLowerCase())));
  return missing.length
    ? [`The form must include one Likert-scale question for each of the following topics: ${missing.join(', ')}.`]
    : [];
};

const getRequiredTextKeywordsValidationErrors = (questions: Question[], assessmentType?: string) => {
  if (assessmentType !== 'arf') return [];
  const texts = questions
    .filter((q) => q.FormatType === 'short-text' || q.FormatType === 'long-text')
    .map((q) => (q.Question || '').toLowerCase());
  const missing = REQUIRED_TEXT_QUESTION_KEYWORDS.filter((keyword) => !texts.some((t) => t.includes(keyword.toLowerCase())));
  return missing.length
    ? [`The form must include one short-text or long-text question for each of the following topics: ${missing.join(', ')}.`]
    : [];
};

export interface BuilderValidationContext {
  assessmentType?: string;
  reviewType?: string;
  /** Enforce the annual-review topic rules only for review forms (survey forms skip them). */
  isReviewForm: boolean;
}

const validateBuilderQuestion = (question: Question, label: string, errors: string[]) => {
  if (!question.Question || question.Question.trim() === '') errors.push(`${label} can't be empty`);

  if ((question.FormatType === 'rating' || question.FormatType === 'nps') && !(question.MaxRating > 0)) {
    errors.push(`${label} MaxRating must be greater than 0`);
  }

  const isChoice = question.FormatType === 'checkboxes' || question.FormatType === 'multiple-choice';
  if (isChoice && (!question.Options?.length || !question.Options.every((option) => option.Label?.trim()))) {
    errors.push(`${label} must have at least one option or non empty option label`);
  }

  if (question.FormatType === 'multiple-choice') {
    question.Options?.forEach((option, optionIndex) => {
      if (option.Action === 'jump_to_section' && !option.TargetSectionId) {
        errors.push(
          `${label}, Option ${optionIndex + 1}: Target section must be selected when "Jump to Section" action is chosen`
        );
      }
    });
  }

  if (question.FormatType === 'likert-scale') {
    if (!question.LikertLabels?.length || !question.LikertLabels.every((l) => l.trim())) {
      errors.push(`${label} LikertLabels can't be empty`);
    }
    if (!question.LikertStatements?.length || !question.LikertStatements.every((s) => s.Statement.trim())) {
      errors.push(`${label} Statements can't be empty`);
    }
  }
};

const validateFlatForm = (questions: Question[], context: BuilderValidationContext): string[] => {
  const errors: string[] = [];
  if (context.isReviewForm) {
    errors.push(
      ...getAnnualPromotionValidationErrors(questions, context.assessmentType, context.reviewType),
      ...getRequiredLikertKeywordsValidationErrors(questions, context.assessmentType),
      ...getRequiredTextKeywordsValidationErrors(questions, context.assessmentType)
    );
  }
  if (!questions.length) errors.push('At least 1 question should be added before submit');
  questions.forEach((question, index) => validateBuilderQuestion(question, `Question ${index + 1}`, errors));
  return errors;
};

export const validateFormDefinition = (definition: FormDefinition, context: BuilderValidationContext): string[] =>
  definition.HasSections
    ? validateSectionedForm(definition.Sections, context)
    : validateFlatForm(definition.FormData, context);

const validateSectionedForm = (sections: Section[], context: BuilderValidationContext): string[] => {
  const errors: string[] = [];
  const allQuestions = sections.flatMap((section) => section.Questions);

  if (context.isReviewForm) {
    errors.push(
      ...getAnnualPromotionValidationErrors(allQuestions, context.assessmentType, context.reviewType),
      ...getRequiredLikertKeywordsValidationErrors(allQuestions, context.assessmentType),
      ...getRequiredTextKeywordsValidationErrors(allQuestions, context.assessmentType)
    );
  }

  if (!sections.length) {
    errors.push('At least 1 section should be added before submit');
    return errors;
  }

  sections.forEach((section, sectionIndex) => {
    if (!section.Questions.length) errors.push(`Section "${section.Title || sectionIndex + 1}" must have at least 1 question`);
    section.Questions.forEach((question, questionIndex) =>
      validateBuilderQuestion(question, `Section ${sectionIndex + 1}, Question ${questionIndex + 1}`, errors)
    );
  });
  return errors;
};

const isLikertStatementUnanswered = (value: string[] | string | undefined) =>
  Array.isArray(value) ? value.length === 0 : !value || `${value}`.trim() === '';

/** Returns the error for one required question, or null when it is answered. */
export const validateQuestionAnswer = (question: Question): string | null => {
  const label = stripMarkdownLinks(question.Question);

  switch (question.FormatType) {
    case 'short-text':
    case 'long-text':
      if (!question.Answer || question.Answer.trim() === '') return `Question "${label}" requires an answer`;
      break;
    case 'rating':
    case 'nps':
      if (typeof question.Rating !== 'number' || question.Rating <= 0) return `Rating required for question "${label}"`;
      break;
    case 'multiple-choice':
      if (!question.McOptionsValue || question.McOptionsValue.trim() === '') return `Answer required for question "${label}"`;
      break;
    case 'checkboxes':
      if (!question.CbOptionsValues?.length) return `At least one option must be selected for "${label}"`;
      break;
    case 'date':
      if (!question.Answer) return `Date required for question "${label}"`;
      break;
    case 'likert-scale':
      if (question.LikertStatements?.length) {
        if (question.LikertStatements.some((statement) => isLikertStatementUnanswered(statement.Value))) {
          return `All statements in "${label}" require an answer`;
        }
      } else if (typeof question.Rating !== 'number' || question.Rating <= 0) {
        return `"Likert scale" rating required for question "${label}"`;
      }
      break;
  }

  if (question.HasComment && question.IsRequired && (!question.Comments || question.Comments.trim() === '')) {
    return `Comment required for question "${label}"`;
  }
  return null;
};

export const validateRequiredAnswers = (questions: Question[]): string[] =>
  questions
    .filter((question) => question.IsRequired)
    .map((question) => validateQuestionAnswer(question))
    .filter((error): error is string => Boolean(error));
